import type { PrizeDropPrizesSchema, PublicCampaignSchema } from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import type { PrizeDrop, PrizeDropPrize } from "./types";

function mapPrizes(
  key: CampaignKey,
  prizes: PrizeDropPrizesSchema["prizes"]
): PrizeDropPrize[] {
  return (
    [...prizes]
      // Ascending quantity ≈ tier order (higher-value prizes are fewer) and a
      // stable order across pushes — quantity is fixed, unlike `left_to_win`.
      .sort((a, b) => a.quantity - b.quantity)
      .map((p) => ({
        // Content-derived (not positional) id, so a backend reorder/insert can't
        // reassign a React key to the wrong tier. `quantity` disambiguates any
        // same-title tiers.
        id: `${key}:${p.title}:${p.quantity}`,
        label: p.title,
        is_catalog_item: p.is_catalog_item,
        quantity: p.quantity,
        left_to_win: p.left_to_win,
        games: p.games,
        games_count: p.games?.length,
      }))
  );
}

/**
 * Join the three streams into the rendered `PrizeDrop[]`. Pure — the store calls
 * it after any relevant stream updates.
 *
 * A DP campaign is included as soon as its `campaigns` tile arrives (so the
 * go-live / opt-in tile is renderable), even before its `prize_drop_prizes` page
 * lands — its `prizes` are simply empty until then.
 */
export function assemblePrizeDrops(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  prizeDropPrizesById: Record<CampaignKey, PrizeDropPrizesSchema>
): PrizeDrop[] {
  const result: PrizeDrop[] = [];

  for (const campaign of Object.values(campaignsById)) {
    if (campaign.type !== "DP") continue;

    const key = campaignKey(campaign);
    const volatile = prizeDropPrizesById[key];
    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;

    result.push({
      campaign,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : { value: 0, currency: "" },
      max_prizes_per_player: volatile?.max_prizes_per_player ?? null,
      prizes: volatile ? mapPrizes(key, volatile.prizes) : [],
      eligible_games: games,
      eligible_games_count: count,
    });
  }

  return result;
}
